import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/** Orders, their metadata and courier tracking — mirrors stacks-agents `src/app/api/orders/`. */
@Injectable({ providedIn: 'root' })
export class OrdersApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API*/
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  // ── read ──

  getOrders(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/orders`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getOrdersCount(projectId: string, type?: string): Promise<number> {
    // Built key by key: an omitted optional would otherwise be sent as the
    // literal string "undefined" and treated as a real filter by the backend.
    let params = new HttpParams().set('projectId', String(projectId))
    if (type !== undefined && type !== null && type !== '') params = params.set('type', String(type))

    return this.http
      .get<{ data?: number }>(`${environment.agentsUrl}v4/retrieval/orders/count`, {
        headers: this.agentsAuthHeaders(),
        params,
      })
      .pipe(
        map(body => body?.data ?? 0),
        catchError(() => of(0)),
      )
      .toPromise() as Promise<number>
  }

  getOrdersPage(projectId: string, page: number, pageSize: number, type?: string, sort = 'id_desc'): Promise<any[]> {
    let params = new HttpParams()
      .set('projectId', String(projectId))
      .set('page', String(page))
      .set('pageSize', String(pageSize))
      .set('sort', String(sort))
    if (type !== undefined && type !== null && type !== '') params = params.set('type', String(type))

    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/orders/page`, {
        headers: this.agentsAuthHeaders(),
        params,
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getOrder(projectId: string, orderId: string): Promise<any[] | null> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/orders/by-id`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, orderId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any[] | null>
  }

  getUserOrders(projectId: string, userId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/orders/by-user`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, userId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getCompletedOrdersSummary(projectId: string, fromDate?: string, toDate?: string): Promise<{ count: number; revenue: number; orders: any[] }> {
    const empty = { count: 0, revenue: 0, orders: [] }
    let params = new HttpParams().set('projectId', String(projectId))
    if (fromDate !== undefined && fromDate !== null && fromDate !== '') params = params.set('fromDate', String(fromDate))
    if (toDate !== undefined && toDate !== null && toDate !== '') params = params.set('toDate', String(toDate))

    return this.http
      .get<{ data?: { count: number; revenue: number; orders: any[] } }>(`${environment.agentsUrl}v4/retrieval/orders/completed-summary`, {
        headers: this.agentsAuthHeaders(),
        params,
      })
      .pipe(
        map(body => body?.data ?? empty),
        catchError(() => of(empty)),
      )
      .toPromise() as Promise<{ count: number; revenue: number; orders: any[] }>
  }

  // ── write ──

  /** Updates order status and appends a history entry. */
  async updateOrderStatus(projectId: string, orderId: string, status: string): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/orders/status`,
        { projectId, orderId, status },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Restores product stock for all items in an order (on cancellation). */
  async restoreOrderStock(projectId: string, products: any[]): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/orders/restore-stock`,
        { projectId, products },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Writes Fincart tracking data into the metadata of the order. */
  async upsertOrderFincartData(projectId: string, orderId: string, fincartData: { fincartId: string; fincartCode: string; trackingUrl: string }): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/order-fincart-data`,
        { projectId, orderId, fincartData },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Writes Aymakan tracking data into the metadata of the order. */
  async upsertOrderAymakanData(projectId: string, orderId: string, aymakanData: { trackingNumber?: string; labelUrl?: string }): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/order-aymakan-data`,
        { projectId, orderId, aymakanData },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Removes a single project_orders_metadata row by key. */
  async deleteOrderMetadata(projectId: string, orderId: string, key: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/order-metadata`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, orderId, key },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes an order (cascades to items, metadata, history). */
  async deleteOrder(projectId: string, orderId: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/orders`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, orderId },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
